import React from 'react';
import * as TopList from 'components/Layout/Tiles/ListContainer';
import { MeasureUnitCode } from 'components/measure-unit/MeasureUnitCode';
import { DataEntry } from 'models/data';
import { Attribute, Measure } from 'services/model/model.service';
import type { TopOpportunityGapCalculator } from 'pages/Benchmarking/common/topOpportunitiesValueExtractors';
import { extractPeerValue, extractTenantValue } from 'pages/Benchmarking/common/topOpportunitiesValueExtractors';
import { NoOpportunityError } from 'pages/Benchmarking/components/NoOpportunityError';
import { useNavigate, useSearchParams } from 'react-router-dom';

interface PerformanceGapListProps {
  selectedBenchmark: string;
  improvementMeasure: Measure;
  groupingAttribute: Attribute;
  dataset: DataEntry[];
  getIcon?: (entry: DataEntry) => React.ReactElement;
  calculateTenantValue?: (improvementMeasure: Measure, currentItem: DataEntry) => number;
  calculatePeerValue?: (improvementMeasure: Measure, currentItem: DataEntry, selectedBenchmark: string) => number;
  calculateGap?: TopOpportunityGapCalculator;
  interpretation: number;
  testId?: string;
  drilldown?: boolean;
}

export function PerformanceGapList({
  dataset,
  selectedBenchmark,
  improvementMeasure,
  groupingAttribute,
  getIcon = undefined,
  calculateTenantValue = extractTenantValue,
  calculatePeerValue = extractPeerValue,
  calculateGap = undefined,
  interpretation,
  testId = '',
  drilldown = false,
}: PerformanceGapListProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const dataSetItems = [...dataset]
    .filter(currentItem => !!currentItem.benchmarks?.[selectedBenchmark])
    .map(currentItem => {
      const tenantValue = calculateTenantValue(improvementMeasure, currentItem);
      const peerValue = calculatePeerValue(improvementMeasure, currentItem, selectedBenchmark);
      const currentGap = calculateGap
        ? calculateGap({
            improvementMeasure,
            currentItem,
            selectedBenchmark,
          })
        : tenantValue - peerValue;
      const financialImpact =
        (peerValue - tenantValue) *
        // If we didn't have any growth the full difference is the potential growth
        (currentItem.values.ordGmvVal !== 0 ? currentItem.values.ordGmvVal : 1) *
        interpretation *
        -1;

      const name = groupingAttribute.getValue(currentItem.attributes);
      return {
        name: name,
        currentGap,
        financialImpact: financialImpact,
        countryCode: groupingAttribute.code === 'chnCountry' ? currentItem.attributes.chnCountry : undefined,
        icon: getIcon ? getIcon(currentItem) : undefined,
        tenantValue: tenantValue,
      };
    });

  if (dataset.length === 0) {
    return <NoOpportunityError />;
  }

  return (
    <div className="flex flex-col justify-between items-center h-[333px]" data-testid={testId}>
      {dataSetItems.length === 0 ? (
        <NoOpportunityError />
      ) : (
        <>
          <TopList.ListContainer className="h-full">
            {dataSetItems.map(listItem => (
              <TopList.ListRow
                key={listItem.name}
                fixedHeight={false}
                onClick={
                  drilldown
                    ? () =>
                        void navigate(
                          `/benchmarking/drilldown/${groupingAttribute.code}/${groupingAttribute.code === 'chnCountry' ? listItem.countryCode : listItem.name}?${searchParams}`
                        )
                    : undefined
                }
              >
                <TopList.NameCell
                  name={listItem.name}
                  color="transparent"
                  icon={listItem.icon}
                  countryCode={listItem.countryCode ? listItem.countryCode.toLowerCase() : undefined}
                />
                <TopList.MetricCell
                  label={
                    improvementMeasure.code === 'ordGmvVal'
                      ? 'benchmarking.benchmarks.opportunities.gap'
                      : 'benchmarking.benchmarks.opportunities.gap-now'
                  }
                  value={listItem.currentGap}
                  unit={MeasureUnitCode.PERCENT}
                  size="lg"
                  signedPositive={listItem.currentGap > 0}
                />
                <TopList.MetricCell
                  label="benchmarking.benchmarks.opportunities.impact"
                  value={listItem.financialImpact}
                  unit={MeasureUnitCode.CURRENCY}
                  size="lg"
                  signedPositive={listItem.financialImpact > 0}
                />
              </TopList.ListRow>
            ))}
          </TopList.ListContainer>
        </>
      )}
    </div>
  );
}
